import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useInventoryAuditLog, type EventType } from "@/hooks/useInventoryAuditLog";
import type { PoolType } from "@/hooks/useProductPools";
import {
  formatInventoryLedgerSourceForDisplay,
  humanizeLedgerSourceSlugsInText,
} from "@/shared/inventoryLedgerSourceDisplay";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Props {
  organisationId: string;
}

type EventFilter = EventType | "all";
type PoolFilter = PoolType | "all";

const PAGE_SIZE = 50;

const InventoryAuditLogTable: React.FC<Props> = ({ organisationId }) => {
  const [page, setPage] = useState(0);
  const [eventFilter, setEventFilter] = useState<EventFilter>("all");
  const [poolFilter, setPoolFilter] = useState<PoolFilter>("all");

  const { data, isFetching, isError, error } = useInventoryAuditLog({
    organisationId,
    eventType: eventFilter === "all" ? undefined : eventFilter,
    poolType: poolFilter === "all" ? undefined : poolFilter,
    page,
    pageSize: PAGE_SIZE,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Inventory Audit Log</CardTitle>
        <CardDescription>
          Every inventory event in chronological order. Snapshot variance rows are per inventory
          source (not a single SKU). Before / after columns are computed from the running ledger
          total per (product, pool).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label className="text-xs">Event type</Label>
            <Select value={eventFilter} onValueChange={(v) => { setEventFilter(v as EventFilter); setPage(0); }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="RECEIVED">RECEIVED</SelectItem>
                <SelectItem value="ADJUSTMENT">ADJUSTMENT</SelectItem>
                <SelectItem value="SNAPSHOT_VARIANCE">SNAPSHOT_VARIANCE</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Pool</Label>
            <Select value={poolFilter} onValueChange={(v) => { setPoolFilter(v as PoolFilter); setPage(0); }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="received">received</SelectItem>
                <SelectItem value="platform">platform</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {isError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            {(error as Error)?.message ?? "Failed to load audit log."}
          </div>
        )}

        <div className="overflow-x-auto rounded-md border">
          <TooltipProvider delayDuration={200}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Effective</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Pool</TableHead>
                <TableHead className="text-right">Before</TableHead>
                <TableHead className="text-right">Δ</TableHead>
                <TableHead className="text-right">After</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>User</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isFetching && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {!isFetching && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                    No events recorded yet.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => {
                const varianceSuffix =
                  r.event_type === "SNAPSHOT_VARIANCE"
                    ? ` (expected ${r.before_quantity.toLocaleString()} vs actual ${r.after_quantity.toLocaleString()})`
                    : "";
                const noteDisplay =
                  r.event_type === "SNAPSHOT_VARIANCE"
                    ? `${r.note ?? ""}${varianceSuffix}`
                    : humanizeLedgerSourceSlugsInText(r.note ?? "");
                const isSnapshotProduct = r.identifier_type.toLowerCase() === "snapshot";
                return (
                <TableRow key={r.id}>
                  <TableCell className="whitespace-nowrap">{format(parseISO(r.effective_date), "yyyy-MM-dd")}</TableCell>
                  <TableCell>
                    {isSnapshotProduct ? (
                      <div className="space-y-0.5">
                        <div className="text-xs text-muted-foreground">Inventory source</div>
                        <div className="text-sm">
                          {formatInventoryLedgerSourceForDisplay(r.identifier_value)}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-0.5">
                        <div className="text-sm">{r.master_product_id}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {r.identifier_type.toUpperCase()} {r.identifier_value}
                        </div>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{r.event_type}</TableCell>
                  <TableCell>{r.pool_type}</TableCell>
                  <TableCell className="text-right">{r.before_quantity.toLocaleString()}</TableCell>
                  <TableCell className={`text-right ${r.quantity < 0 ? "text-red-600" : "text-green-600"}`}>
                    {r.quantity > 0 ? "+" : ""}{r.quantity.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">{r.after_quantity.toLocaleString()}</TableCell>
                  <TableCell className="max-w-[300px] text-sm text-muted-foreground">
                    {noteDisplay.trim() ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block max-w-[300px] cursor-default truncate border-b border-dotted border-muted-foreground/40">
                            {noteDisplay}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-md whitespace-pre-wrap break-words text-left">
                          {noteDisplay}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {(
                      r.created_by_email === "system-backfill@figabl.local" ||
                      r.created_by_email === "system-snapshot@figabl.local"
                    ) ? "system" : (r.created_by_email ?? "")}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </TooltipProvider>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page + 1} of {totalPages} · {total.toLocaleString()} events
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0 || isFetching}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page + 1 >= totalPages || isFetching}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InventoryAuditLogTable;
